import React, { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import {
    useActivateReportingMutation,
    useCreateReportingMutation,
    useGetDocumentQuery,
    useGetInvestmentFundsQuery,
    useGetReportingsQuery,
    useTestReportingMutation,
    useUpdateReportingMutation,
} from 'api/main';
import { billStatus as reportingStatus } from 'utils/constants';
import {
    getFileExtension,
    handleDocumentPreview,
    parseFullName,
    validateFileSize,
} from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    ActionsMenu,
    ConfirmDialog,
    CustomDatePicker,
    CustomSelect,
    CustomTextField,
    ErrorMessage,
    FileErrorMessage,
    FileInputButton,
    ListContainer,
    Loader,
    CustomPaper,
} from 'components';
import { MenuActivate, MenuTest } from 'components/ActionsMenu/actions';
import {
    Box,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

const ActivateActionItem = React.forwardRef((props, ref) => <MenuActivate ref={ref} {...props} />);
const TestActionItem = React.forwardRef((props, ref) => <MenuTest ref={ref} {...props} />);

const getDefaultForm = () => ({
    fundId: '',
    recipient: 'subscribers',
    subject: '',
    date: moment(),
    file: '',
});

const boxSx = theme => ({
    margin: '0 auto',
    width: '40%',
    [theme.breakpoints.down('md')]: { width: '100%' },
});

const ServicerReporting = () => {
    const {
        data: fundList = [],
        isFetching: fundsLoader,
        isSuccess: fundsFetched,
    } = useGetInvestmentFundsQuery({
        visible: 1,
    });

    const { data: reportings = [], isLoading: dataLoader } = useGetReportingsQuery({
        pagination: false,
    });
    const [createReporting, { isLoading: createLoading }] = useCreateReportingMutation();
    const [updateReporting, { isLoading: updateLoading }] = useUpdateReportingMutation();
    const [testReporting, { isLoading: testLoading }] = useTestReportingMutation();
    const [
        activateReporting,
        { isLoading: activateLoading, isSuccess: activateSuccess, reset: activateReset },
    ] = useActivateReportingMutation();

    const [form, setForm] = useState(getDefaultForm());
    const [fileError, setFileError] = useState('');
    const [formError, setFormError] = useState({ subject: '', file: '' });
    const [downloadingDoc, setDownloadingDoc] = useState(null);
    const [selectedReportingDate, setSelectedReportingDate] = useState('');
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);

    const latestReporting = useMemo(
        () => reportings.find(r => r.status === reportingStatus.CREATED),
        [reportings]
    );

    const { data: latestDoc, isLoading: docLoading } = useGetDocumentQuery(
        latestReporting?.document?.id,
        { skip: !latestReporting?.document?.id }
    );

    const activatedReportings = useMemo(
        () => reportings.filter(r => r.status === reportingStatus.ACTIVATED),
        [reportings]
    );

    const completedReportings = useMemo(
        () => reportings.filter(r => r.status === reportingStatus.COMPLETED),
        [reportings]
    );

    const filteredReportings = useMemo(
        () =>
            [...activatedReportings, ...completedReportings].filter(r =>
                moment(r.date).isSame(moment(selectedReportingDate, 'L'), 'day')
            ),
        [selectedReportingDate, activatedReportings, completedReportings]
    );

    const reportingUniqueDates = useMemo(() => {
        const dates = [...activatedReportings, ...completedReportings].map(r =>
            moment(r.date).format('L')
        );
        const datesArray = [...new Set(dates)];
        return datesArray.map(d => moment(d, 'L').toDate());
    }, [activatedReportings, completedReportings]);

    const defaultFormValue = useMemo(() => {
        if (latestReporting) {
            return {
                fundId: latestReporting.investmentFund.id,
                recipient: latestReporting.recipientType,
                subject: latestReporting.emailSubject,
                date: moment(latestReporting.date),
                file: latestReporting.document,
            };
        } else return getDefaultForm();
    }, [latestReporting]);

    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setForm(prevState => ({ ...prevState, [key]: value }));
        },
        []
    );

    const handleDateChange = useCallback(newDate => {
        setForm(prevState => ({
            ...prevState,
            date: newDate.startOf('day').add(newDate.utcOffset(), 'm'),
        }));
    }, []);

    const handleFileChange = useCallback(e => {
        const file = e.target.files[0];
        if (getFileExtension(file) === 'pdf') {
            if (validateFileSize(file)) {
                setForm(prevState => ({ ...prevState, file }));
                setFileError('');
            } else {
                setFileError('size');
            }
        } else {
            setFileError('type');
        }
    }, []);

    const changesMade = useMemo(() => !isEqual(defaultFormValue, form), [form, defaultFormValue]);

    const handleSaveData = useCallback(() => {
        if (!!form.subject.trim()) {
            setFormError(prevState => ({ ...prevState, subject: '' }));
            if (latestReporting) {
                updateReporting({ ...form, id: latestReporting.id });
            } else {
                if (!form.file) {
                    setFormError(prevState => ({ ...prevState, file: texts.FILE_MISSING }));
                } else {
                    setFormError(prevState => ({ ...prevState, file: '' }));
                    createReporting(form);
                }
            }
        } else {
            setFormError(prevState => ({ ...prevState, subject: texts.SAISIR_LE_CHAMP }));
        }
    }, [createReporting, updateReporting, form, latestReporting]);

    const handleTest = useCallback(() => {
        testReporting(latestReporting.id);
    }, [testReporting, latestReporting]);

    const handleActivate = useCallback(() => {
        activateReporting(latestReporting.id);
    }, [activateReporting, latestReporting]);

    const handlePreviewDoc = useCallback(doc => {
        handleDocumentPreview(doc, setDownloadingDoc);
    }, []);

    const handleChangeReportingDate = useCallback(
        e => setSelectedReportingDate(e.target.value),
        []
    );

    useEffect(() => {
        if (activateSuccess) {
            setActivateDialogOpen(false);
            activateReset();
        }
    }, [activateSuccess, activateReset]);

    useEffect(() => {
        setForm(defaultFormValue);
    }, [defaultFormValue]);

    useEffect(() => {
        if (fundsFetched) {
            setForm(prevState => ({
                ...prevState,
                fundId: latestReporting ? prevState.fundId : fundList[0].id,
            }));
        }
    }, [fundList, fundsFetched, latestReporting]);

    useEffect(() => {
        if (reportingUniqueDates.length) {
            setSelectedReportingDate(reportingUniqueDates[0]);
        }
    }, [reportingUniqueDates]);

    return (
        <ListContainer>
            <Typography variant="h5" align="left" gutterBottom>
                {texts.REPORTING}
            </Typography>
            <ConfirmDialog
                open={activateDialogOpen}
                title={texts.REPORTING_ACTIVATION}
                cancelText={texts.CANCEL}
                confirmText={texts.CONFIRMER}
                loading={activateLoading}
                handleClose={() => setActivateDialogOpen(false)}
                handleConfirm={handleActivate}
            >
                {texts.CONFIRM_REPORTING_ACTIVATION}
            </ConfirmDialog>
            <CustomPaper>
                <Loader loading={fundsLoader || dataLoader || docLoading}>
                    <Box sx={boxSx}>
                        <CustomSelect
                            label={texts.FUND}
                            fullWidth
                            value={form.fundId}
                            onChange={handleChange('fundId')}
                        >
                            {fundList.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <CustomSelect
                            label={texts.RECIPIENTS}
                            fullWidth
                            value={form.recipient}
                            onChange={handleChange('recipient')}
                        >
                            <MenuItem value="subscribers">{texts.SUBSCRIBERS}</MenuItem>
                            <MenuItem value="distributors">{texts.DISTRIBUTORS}</MenuItem>
                        </CustomSelect>
                        <CustomTextField
                            label={texts.SUBJECT}
                            fullWidth
                            value={form.subject}
                            error={formError.subject}
                            onChange={handleChange('subject')}
                        />
                        <CustomDatePicker
                            label={texts.DATE}
                            fullWidth
                            value={form.date}
                            onChange={handleDateChange}
                        />
                        <Typography variant="body2" gutterBottom>
                            {form.file?.name || (latestReporting ? latestDoc?.name : '')}
                        </Typography>
                        <FileInputButton
                            id="reporting-document"
                            showIcon
                            noMargin
                            label={texts.AJOUTER_UN_DOCUMENT}
                            onFileChange={handleFileChange}
                        />
                        <ErrorMessage message={formError.file} />
                        {!!fileError && <FileErrorMessage variant={fileError} useMargin={false} />}
                    </Box>
                    <Box textAlign="center" paddingTop={4}>
                        <Loader loading={createLoading || updateLoading} marginTop={0}>
                            <ActionButton
                                disabled={createLoading || updateLoading || !changesMade}
                                onClick={handleSaveData}
                                variant="contained"
                            >
                                {texts.SAVE}
                            </ActionButton>
                        </Loader>
                    </Box>
                    <Box paddingTop={4} mb={5}>
                        {!(createLoading || updateLoading) && (
                            <ActionsMenu
                                autoClose
                                justify="center"
                                buttonStyle="text"
                                buttonText={texts.ENVOYER}
                                buttonProps={{ disabled: changesMade }}
                            >
                                <ActivateActionItem
                                    disabled={
                                        activateLoading ||
                                        testLoading ||
                                        !!activatedReportings.length
                                    }
                                    onClick={() => setActivateDialogOpen(true)}
                                />
                                <TestActionItem
                                    disabled={testLoading || activateLoading}
                                    onClick={handleTest}
                                />
                            </ActionsMenu>
                        )}
                    </Box>
                    <Box sx={boxSx}>
                        <CustomSelect
                            fullWidth
                            label={texts.REPORTING_HISTORY}
                            value={selectedReportingDate}
                            onChange={handleChangeReportingDate}
                        >
                            {reportingUniqueDates.map((d, i) => (
                                <MenuItem key={i} value={d}>
                                    {moment(d).format('L')}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{texts.SENDING_DATE}</TableCell>
                                    <TableCell>{texts.REPORTING_DATE}</TableCell>
                                    <TableCell>{texts.CREATOR}</TableCell>
                                    <TableCell>{texts.FUND}</TableCell>
                                    <TableCell>{texts.RECIPIENTS}</TableCell>
                                    <TableCell>{texts.SUBJECT}</TableCell>
                                    <TableCell>{texts.DOCUMENT}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredReportings.map(r => (
                                    <TableRow key={r.id}>
                                        <TableCell>{moment(r.activatedAt).format('L')}</TableCell>
                                        <TableCell>{moment(r.date).format('L')}</TableCell>
                                        <TableCell>{parseFullName(r.activatedBy)}</TableCell>
                                        <TableCell>{r.investmentFund.name}</TableCell>
                                        <TableCell>
                                            {r.recipientType === 'subscribers'
                                                ? texts.SUBSCRIBERS
                                                : texts.DISTRIBUTORS}
                                        </TableCell>
                                        <TableCell>{r.emailSubject}</TableCell>
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        textDecoration: 'underline',
                                                    },
                                                }}
                                                onClick={() => handlePreviewDoc(r.document)}
                                                component="span"
                                            >
                                                <Loader
                                                    loading={downloadingDoc === r.document.id}
                                                    size={20}
                                                    marginTop={0}
                                                >
                                                    {r.document.name}
                                                </Loader>
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Loader>
            </CustomPaper>
        </ListContainer>
    );
};

export default ServicerReporting;
